import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { tripsApi } from '@/services/api';
import { ROUTES } from '@/routes';
import { formatDate } from '@tally/shared';
import { formatUsd, localSummary } from '@/utils/money';
import { Plane, MapPin, Calendar, Filter, Globe2 } from 'lucide-react';
import { motion } from 'framer-motion';

export function TripsPage() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [scope, setScope] = useState<string>('');
  const { data, isLoading } = useQuery({
    queryKey: ['trips', statusFilter, scope],
    queryFn: () => tripsApi.list({ ...(statusFilter ? { status: statusFilter } : {}), ...(scope ? { scope } : {}), pageSize: '100' }),
  });

  const trips = data?.data?.items || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Trips</h1>
          <p className="text-sm text-muted-foreground">Your travel history, auto-detected and organized</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="rounded-lg border border-input bg-background px-3 py-1.5 text-sm"
            aria-label="Domestic or international"
          >
            <option value="">Domestic & international</option>
            <option value="international">International</option>
            <option value="domestic">Domestic</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-input bg-background px-3 py-1.5 text-sm"
          >
            <option value="">All trips</option>
            <option value="detected">Detected</option>
            <option value="confirmed">Confirmed</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-48 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : trips.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16">
          <Plane className="mb-4 h-12 w-12 text-muted-foreground/50" />
          <h3 className="text-lg font-medium">No trips yet</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Trips appear once your email scan finds bookings or purchases away from home. Add where you've lived in Settings → People & Home History.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {trips.map((trip: any, i: number) => (
            <motion.div
              key={trip.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Link
                to={ROUTES.app.trip(trip.id)}
                className="group block rounded-xl border border-border bg-card p-5 transition-all hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5"
              >
                <div className="mb-3 flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold group-hover:text-primary">{trip.name}</h3>
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">
                        {trip.stops?.length > 0
                          ? trip.stops.map((s: any) => s.city || s.country).filter(Boolean).join(' → ')
                          : `${trip.destination?.city || 'Unknown'}${trip.destination?.country ? `, ${trip.destination.country}` : ''}`}
                      </span>
                      {trip.international && <Globe2 className="h-3 w-3 shrink-0" aria-label="International" />}
                    </div>
                  </div>
                  <StatusBadge status={trip.status} />
                </div>

                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(trip.startDate)} — {formatDate(trip.endDate)}
                  </span>
                  {trip.travelers?.length > 0 && (
                    <span className="flex -space-x-1.5" title={trip.travelers.map((t: any) => t.name).join(', ')}>
                      {trip.travelers.slice(0, 4).map((t: any) => (
                        <span key={t.id} className="flex h-5 w-5 items-center justify-center rounded-full border border-card bg-primary/15 text-[10px] font-medium text-primary">
                          {t.name.charAt(0)}
                        </span>
                      ))}
                    </span>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                  <div>
                    <span className="text-lg font-bold">{formatUsd(trip.totalExpenses)}</span>
                    {localSummary(trip.totalsByCurrency) && (
                      <p className="text-xs text-muted-foreground">{localSummary(trip.totalsByCurrency)}</p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">{trip.transactionCount} transactions</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    detected: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
    confirmed: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400',
    in_progress: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400',
    completed: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  };

  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[status] || styles.completed}`}>
      {status.replace('_', ' ')}
    </span>
  );
}
